import React, { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { useNavigate } from 'react-router-dom';
import { Progress, Tag } from 'antd';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, ArcElement,
  Tooltip, Legend, Filler
} from 'chart.js';
import { getVentesRapport } from '../../services/rapportService';
// Pont jetons CSS → canvas : les graphiques lisent la charte au lieu de figer
// des couleurs. Le jour où la charte bascule, ils suivent sans être retouchés.
import { jetons, alpha, palette, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import VentesClienteModal from './VentesClienteModal';
import './dashboardAdmin.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend, Filler);

// ── Sous-composants ─────────────────────────────────────────────

function HeroBlock({ hero }) {
  if (!hero) return null;
  const { ca_net_jour = 0, variation_pct, paires_nettes = 0, phrase } = hero;
  const varSign = variation_pct > 0 ? '+' : '';
  const varColor = variation_pct >= 0 ? 'var(--ndoe-success)' : 'var(--ndoe-danger)';

  return (
    <div className="da-hero">
      <div className="da-hero__eyebrow">CA NET AUJOURD'HUI</div>
      <div className="da-hero__amount">
        <CountUp end={Math.round(ca_net_jour)} duration={1.1} separator=" " />
        <span className="da-hero__currency"> $</span>
      </div>
      <div className="da-hero__meta">
        {variation_pct !== null && variation_pct !== undefined && (
          <span className="da-hero__var" style={{ color: varColor }}>
            {varSign}{variation_pct}% vs hier
          </span>
        )}
        <span className="da-hero__sep">·</span>
        <span>{paires_nettes} paire{paires_nettes !== 1 ? 's' : ''} nette{paires_nettes !== 1 ? 's' : ''}</span>
      </div>
      {phrase && <div className="da-hero__phrase">{phrase}</div>}
    </div>
  );
}

function KpiCard({ kpi }) {
  const { key, val = 0, unit, trend } = kpi;
  const labels = {
    ca_30j:            'CA 30 jours',
    ca_7j:             'CA 7 jours',
    panier_moyen:      'Panier moyen',
    commandes_actives: 'Commandes actives',
    paires_circ:       'Paires en circulation',
    retours_30j:       'Retours 30j',
    echanges_ouverts:  'Échanges ouverts'
  };
  const isCurrency = unit === '$' || unit === '$/paire';

  return (
    <div className="da-kpi-card">
      <div className="da-kpi-card__label">{labels[key] || key}</div>
      <div className="da-kpi-card__value">
        <CountUp end={isCurrency ? Math.round(val) : Number(val) || 0} duration={0.9} separator=" " decimals={!isCurrency && val % 1 ? 1 : 0} />
        {unit && <span className="da-kpi-card__unit"> {unit}</span>}
      </div>
      {trend !== null && trend !== undefined && (
        <div className="da-kpi-card__trend" style={{ color: trend >= 0 ? 'var(--ndoe-success)' : 'var(--ndoe-danger)' }}>
          {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
}

function ChartCa({ data30j }) {
  if (!data30j || data30j.length === 0) return (
    <div className="da-chart-empty">Aucune donnée disponible</div>
  );

  const labels = data30j.map(d => {
    const dt = new Date(d.date);
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  });

  // Couleurs lues dans la CHARTE (pont chartTheme), plus écrites en dur.
  // Avant : borderColor '#C9A227' et ticks '#5A6A7E' — les valeurs de l'ancien
  // jeu de jetons d'index.css, celui qui PERDAIT la cascade et a été supprimé
  // au lot 1. La courbe était donc dorée d'un or (#C9A227) que plus rien
  // d'autre dans l'app n'utilisait, à côté d'une interface en #c28a27.
  const t = jetons();
  const chartData = {
    labels,
    datasets: [{
      label: 'CA net ($)',
      data: data30j.map(d => d.ca_net),
      borderColor: t.gold,
      backgroundColor: alpha(t.gold, 0.10),
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.35,
      fill: true
    }]
  };

  // optionsBase apporte la grille, les axes, l'info-bulle aux couleurs de la
  // charte et une animation sobre (420 ms, coupée si l'utilisateur a demandé
  // moins d'animations au niveau du système).
  const options = fusionner(optionsBase(), {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `${Math.round(ctx.parsed.y).toLocaleString('fr-CD')} $`
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 11 } } },
      y: {
        beginAtZero: true,
        grid: { color: t.hairline, drawTicks: false },
        // GRADUATION — signalé par le propriétaire : « 0k et 1k plusieurs fois
        // sur la graduation crée de la confusion ».
        //
        // La cause : `Math.round(v / 1000) + 'k'`. Avec un maximum autour de
        // 1 000 $, les paliers tombent à 0 / 250 / 500 / 750 / 1 000 et
        // l'arrondi au millier les écrase en « 0k · 0k · 1k · 1k · 1k ». Cinq
        // repères, deux libellés : l'axe ne gradue plus rien.
        //
        // On choisit l'unité d'après l'ÉCHELLE, et jamais au coup par coup :
        // sous 1 000 $ on écrit le montant tel quel (250, 500, 750) ; au-dessus,
        // en milliers avec la précision nécessaire pour que deux paliers
        // voisins restent distincts. Deux libellés identiques deviennent
        // impossibles par construction.
        ticks: {
          color: t.muted,
          font: { size: 11 },
          callback(v, index, paliers) {
            if (!v) return '0';
            const max = Math.max(...paliers.map((p) => Math.abs(p.value)), 0);
            if (max < 1000) return String(Math.round(v));
            // Les décimales se déduisent du PAS, et sont juste assez nombreuses
            // pour l'écrire EXACTEMENT. Un pas de 250 $ vaut 0,25k : arrondir à
            // une décimale donnerait « 0,3k », soit une graduation fausse — le
            // défaut d'origine, en plus discret.
            const pas = paliers.length > 1
              ? Math.abs(paliers[1].value - paliers[0].value) / 1000 : 1;
            let dec = 0;
            while (dec < 2 && Number(pas.toFixed(dec)) !== pas) dec += 1;
            return `${(v / 1000).toFixed(dec).replace('.', ',')}k`;
          },
        },
        border: { display: false },
      }
    }
  });

  return (
    <div className="da-chart-wrap">
      <Line data={chartData} options={options} />
    </div>
  );
}


// Mix des ventes 30 j (moteur Ventes premium) : donut catégories + parts de
// marques. Chargé à part — une panne du moteur fait disparaître le bloc,
// jamais le tableau de bord.
// (La palette est lue DANS le rendu, pas ici : au chargement du module, le CSS
//  n'est pas garanti appliqué et getComputedStyle rendrait du vide — le pont
//  retomberait en silence sur ses valeurs de repli et ne servirait à rien.)
function MixVentes() {
  const [mix, setMix] = useState(null);
  useEffect(() => {
    let ok = true;
    getVentesRapport({ period: 30 })
      .then(({ data }) => { if (ok) setMix(data); })
      .catch(() => { if (ok) setMix(null); });
    return () => { ok = false; };
  }, []);
  if (!mix || !mix.categories || !mix.categories.length) return null;
  // Palette catégorielle issue de la CHARTE : elle alterne les familles plutôt
  // que de dégrader une seule teinte — un dégradé de dorés est illisible sur un
  // téléphone en plein soleil, et indistinct pour un daltonien.
  const COULEURS_MIX = palette();
  const cats = mix.categories.slice(0, 7);
  const marques = mix.marques.slice(0, 5);
  const maxM = marques[0] ? marques[0].ca : 1;
  return (
    <div className="da-mix">
      <div className="da-mix__col">
        <div className="da-mix__cap">Catégories · 30 j</div>
        <div className="da-mix__donut">
          <Doughnut
            data={{
              labels: cats.map((c) => c.categorie),
              datasets: [{
                data: cats.map((c) => c.ca),
                backgroundColor: cats.map((_, i) => COULEURS_MIX[i % COULEURS_MIX.length]),
                borderColor: jetons().paper,   /* sépare les parts sans trait noir */
                borderWidth: 2,
              }],
            }}
            options={fusionner(optionsBase({ axeY: false }), {
              cutout: '62%',
              // 1000 ms donnaient une impression de lenteur sur un téléphone
              // d'entrée de gamme, pour un écran consulté des dizaines de fois
              // par jour. optionsBase impose 420 ms — et coupe l'animation si
              // l'utilisateur a demandé moins de mouvement au niveau système.
              plugins: {
                tooltip: { callbacks: { label: (i) => ' ' + i.label + ' : ' + Math.round(i.parsed).toLocaleString('fr-CD') + ' USD' } },
              },
            })}
          />
        </div>
      </div>
      {/* Top marques : rangée DÉDIÉE (.da-mix-row) — avant, réutiliser
          .da-top-row--compact (qui attend une cellule « rang » en 1re colonne de
          20px) écrasait le nom de la marque dans cette colonne étroite. */}
      <div className="da-mix__col">
        <div className="da-mix__cap">Top marques · 30 j</div>
        <div className="da-mix__marques">
          {marques.map((m) => (
            <div key={m.marque} className="da-mix-row">
              <span className="da-mix-row__name" title={m.marque}>{m.marque}</span>
              <div className="da-mix-row__bar-wrap">
                <div className="da-mix-row__bar" style={{ width: Math.max(4, Math.round((m.ca / maxM) * 100)) + '%' }} />
              </div>
              <span className="da-mix-row__val">{m.part_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FunnelOps({ funnel }) {
  if (!funnel) return null;
  // Couleurs lues dans la charte. Avant : 5 valeurs en dur, dont 4 issues du
  // jeu de jetons MORT (#0B1F44, #C9A227, #6F8B74) — cet entonnoir était donc
  // peint dans une palette que plus rien d'autre dans l'app n'utilisait.
  // NB : « En cours » et « Livrées » partageaient LA MÊME couleur (#6F8B74),
  // ce qui rendait les deux dernières étapes indistinguables. Corrigé : la
  // progression va du bleu nuit (début) au vert (issue réussie).
  const t = jetons();
  const steps = [
    { label: 'Commandes actives',  val: funnel.commandes_actives,  color: t.ink },
    { label: 'Affectées',          val: funnel.affectees,          color: t.royal },
    { label: 'Prises en charge',   val: funnel.prises_en_charge,   color: t.gold },
    { label: 'En cours',           val: funnel.en_cours,           color: t.info },
    { label: 'Livrées (30j)',      val: funnel.livrees_30j,        color: t.success }
  ];
  const max = Math.max(...steps.map(s => s.val || 0), 1);

  return (
    <div className="da-funnel">
      {steps.map((s, i) => (
        <div key={i} className="da-funnel-step">
          <div className="da-funnel-step__label">{s.label}</div>
          <div className="da-funnel-step__bar-wrap">
            <div
              className="da-funnel-step__bar"
              style={{ width: `${Math.round((s.val || 0) / max * 100)}%`, background: s.color }}
            />
          </div>
          <div className="da-funnel-step__val">{s.val || 0}</div>
        </div>
      ))}
      {funnel.taux_livraison !== undefined && (
        <div className="da-funnel-rate">
          Taux de livraison (30j) : <strong>{Math.round(funnel.taux_livraison * 100)}%</strong>
        </div>
      )}
    </div>
  );
}

function StockHealth({ stock, health }) {
  if (!stock) return null;
  const max = stock.comptable || 1;
  return (
    <div className="da-stock-health">
      <div className="da-stock-health__gauges">
        <div className="da-gauge-row">
          <span className="da-gauge-label">Disponible</span>
          <Progress percent={Math.round((stock.disponible || 0) / max * 100)} strokeColor="var(--ndoe-success)" size="small" showInfo={false} />
          <span className="da-gauge-val">{stock.disponible || 0} / {stock.comptable || 0}</span>
        </div>
        <div className="da-gauge-row">
          <span className="da-gauge-label">Réservé</span>
          <Progress percent={Math.round((stock.reservee || 0) / max * 100)} strokeColor="var(--ndoe-gold)" size="small" showInfo={false} />
          <span className="da-gauge-val">{stock.reservee || 0}</span>
        </div>
        <div className="da-gauge-row">
          <span className="da-gauge-label">En circulation</span>
          <Progress percent={Math.round((stock.en_circulation || 0) / max * 100)} strokeColor="var(--ndoe-royal)" size="small" showInfo={false} />
          <span className="da-gauge-val">{stock.en_circulation || 0}</span>
        </div>
      </div>
      {health && (
        <div className="da-stock-health__badges">
          {health.ruptures > 0 && <Tag color="red">{health.ruptures} rupture{health.ruptures > 1 ? 's' : ''}</Tag>}
          {health.faible_stock > 0 && <Tag color="orange">{health.faible_stock} stock faible</Tag>}
          {stock.sur_engagement > 0 && <Tag color="purple">{stock.sur_engagement} sur-engagement</Tag>}
          {health.ruptures === 0 && health.faible_stock === 0 && stock.sur_engagement === 0 && (
            <Tag color="green">Stock sain</Tag>
          )}
        </div>
      )}
      {health && health.top_ruptures && health.top_ruptures.length > 0 && (
        <div className="da-ruptures">
          <div className="da-ruptures__title">Ruptures prioritaires</div>
          {health.top_ruptures.map((r, i) => (
            <div key={i} className="da-rupture-item">
              <span className="da-rupture-item__name">{r.produit}</span>
              <span className="da-rupture-item__meta">{r.pointure} · {r.couleur}</span>
              <span className="da-rupture-item__vendu">{r.vendu_30j} vendu/30j</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopRankings({ top }) {
  if (!top) return null;
  const maxP  = (top.produits[0]?.qte_30j)  || 1;
  const maxPt = (top.pointures[0]?.qte_30j) || 1;
  const maxC  = (top.couleurs[0]?.qte_30j)  || 1;

  return (
    <div className="da-tops">
      <div className="da-top-section">
        <div className="da-top-section__title">Top modèles — 30j</div>
        {top.produits.map((p, i) => (
          <div key={i} className="da-top-row">
            <span className="da-top-row__rank">{i + 1}</span>
            <div className="da-top-row__info">
              <span className="da-top-row__name">{p.nom}</span>
              <span className="da-top-row__sub">{p.marque}</span>
            </div>
            <div className="da-top-row__bar-wrap">
              <div className="da-top-row__bar" style={{ width: `${Math.round(p.qte_30j / maxP * 100)}%` }} />
            </div>
            <span className="da-top-row__val">{p.qte_30j} p.</span>
            {p.ca_30j !== undefined && <span className="da-top-row__ca">{Math.round(p.ca_30j).toLocaleString('fr-CD')} $</span>}
          </div>
        ))}
      </div>

      <div className="da-top-cols">
        <div className="da-top-section">
          <div className="da-top-section__title">Top pointures</div>
          {top.pointures.map((p, i) => (
            <div key={i} className="da-top-row da-top-row--compact">
              <span className="da-top-row__rank">{i + 1}</span>
              <span className="da-top-row__name">{p.pointure}</span>
              <div className="da-top-row__bar-wrap">
                <div className="da-top-row__bar da-top-row__bar--gold" style={{ width: `${Math.round(p.qte_30j / maxPt * 100)}%` }} />
              </div>
              <span className="da-top-row__val">{p.qte_30j}</span>
            </div>
          ))}
        </div>
        <div className="da-top-section">
          <div className="da-top-section__title">Top couleurs</div>
          {top.couleurs.map((c, i) => (
            <div key={i} className="da-top-row da-top-row--compact">
              <span className="da-top-row__rank">{i + 1}</span>
              <span className="da-top-row__name">{c.couleur}</span>
              <div className="da-top-row__bar-wrap">
                <div className="da-top-row__bar da-top-row__bar--sage" style={{ width: `${Math.round(c.qte_30j / maxC * 100)}%` }} />
              </div>
              <span className="da-top-row__val">{c.qte_30j}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AlertList({ alerts }) {
  const navigate = useNavigate();
  if (!alerts || alerts.length === 0) return (
    <div className="da-no-alerts">Aucune alerte active ✓</div>
  );

  return (
    <div className="da-alerts">
      {alerts.map((a, i) => (
        <button
          key={i}
          className={`da-alert-item da-alert-item--${a.severity}`}
          onClick={() => a.link && navigate(a.link)}
        >
          <span className="da-alert-item__msg">{a.message}</span>
          {a.link && <span className="da-alert-item__arrow">→</span>}
        </button>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="da-section">
      <h2 className="da-section__title">{title}</h2>
      <div className="da-section__body">{children}</div>
    </section>
  );
}

// ── Temps réel : « combien je fais aujourd'hui vs la moyenne » ──
// Répond immédiatement aux 4 questions de pilotage : commandes du jour vs moyenne
// (mois courant / mois précédent), réalisé vs attendu du jour, pipeline confirmé,
// fluidité des opérations. Absorbe l'ancienne bande « Aujourd'hui » (encaissé /
// essai / corrections) pour ne pas dupliquer l'info.
function TempsReelBlock({ tr, jour }) {
  // Repli si l'API ne renvoie pas (encore) temps_reel (front déployé avant l'API, ou
  // rollback API seul) : on garde l'ancienne bande « Aujourd'hui » depuis `jour`.
  if (!tr) {
    if (!jour) return null;
    return (
      <div className="da-jour">
        <div className="da-jour__cell">
          <span className="da-jour__lbl">Encaissé aujourd’hui</span>
          <span className="da-jour__val">{Math.round(Number(jour.encaisse_jour) || 0).toLocaleString('fr-FR')} $</span>
        </div>
        <div className="da-jour__cell">
          <span className="da-jour__lbl">Paires à l’essai (chez les clientes)</span>
          <span className="da-jour__val">{jour.paires_essai || 0}</span>
        </div>
        <div className="da-jour__cell">
          <span className="da-jour__lbl">Corrections / régularisations du jour</span>
          <span className="da-jour__val">{jour.corrections_jour || 0}</span>
        </div>
      </div>
    );
  }
  const $ = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} $`;
  const cmd = tr.commandes || {};
  const liv = tr.en_livraison || {};
  const j = tr.jour || {};
  const flu = tr.fluidite || {};

  // Delta du jour vs la moyenne/jour du mois en cours.
  const deltaPct = cmd.moy_mois > 0 ? Math.round(((cmd.jour - cmd.moy_mois) / cmd.moy_mois) * 100) : null;
  const deltaColor = deltaPct == null ? 'var(--ndoe-muted)' : deltaPct >= 0 ? 'var(--ndoe-success)' : 'var(--ndoe-danger)';

  // Par livreur — aujourd'hui (trié CA desc côté serveur ; total pré-calculé).
  const pl = Array.isArray(tr.par_livreur?.livreurs) ? tr.par_livreur.livreurs : [];
  const plTotal = tr.par_livreur?.total || null;

  // Vrai pourcentage (peut dépasser 100 % : une vente/un encaissement du jour peut venir
  // d'une commande antérieure). La BARRE est bornée à 100, le CHIFFRE affiché est réel.
  const pctPaires = j.paires_commandees > 0 ? Math.round((j.paires_vendues / j.paires_commandees) * 100) : null;
  const pctMontant = j.montant_attendu > 0 ? Math.round((j.encaisse / j.montant_attendu) * 100) : null;

  // Fluidité : verdict simple et lisible (seuils volontairement prudents).
  const fluChips = [
    { l: 'Prise en charge', v: flu.prise_en_charge_h == null ? '—' : `${flu.prise_en_charge_h} h`, ok: flu.prise_en_charge_h != null && flu.prise_en_charge_h <= 24 },
    { l: 'Livraison', v: flu.livraison_h == null ? '—' : `${flu.livraison_h} h`, ok: flu.livraison_h != null && flu.livraison_h <= 48 },
    { l: 'Taux de livraison (7 j)', v: flu.taux_livraison_pct == null ? '—' : `${flu.taux_livraison_pct} %`, ok: flu.taux_livraison_pct != null && flu.taux_livraison_pct >= 70 },
    { l: 'Reports (7 j)', v: flu.reports_7j ?? 0, ok: (flu.reports_7j || 0) <= 5 },
    { l: 'En retard', v: flu.en_retard ?? 0, ok: (flu.en_retard || 0) === 0 },
    { l: 'Corrections (jour)', v: jour?.corrections_jour ?? 0, ok: (jour?.corrections_jour || 0) <= 3 },
  ];

  return (
    <div className="da-tr">
      <div className="da-tr__card da-tr__card--cmd">
        <span className="da-tr__lbl">Commandes créées aujourd’hui</span>
        <span className="da-tr__big">
          <CountUp end={cmd.jour || 0} duration={0.8} />
          {deltaPct != null && <small style={{ color: deltaColor }}> {deltaPct >= 0 ? '+' : ''}{deltaPct}% vs moy.</small>}
        </span>
        <span className="da-tr__sub">
          Moy. mois en cours : <b>{cmd.moy_mois == null ? '—' : `${cmd.moy_mois}/j`}</b> · mois dernier : <b>{cmd.moy_mois_prec == null ? '—' : `${cmd.moy_mois_prec}/j`}</b>
        </span>
        <span className="da-tr__sub">{cmd.total_mois ?? 0} ce mois · {cmd.total_mois_prec ?? 0} le mois dernier</span>
      </div>

      <div className="da-tr__card">
        <span className="da-tr__lbl">Réalisé vs attendu — aujourd’hui</span>
        <div className="da-tr__ratio">
          <span><b>{j.paires_vendues ?? 0}</b> vendues / {j.paires_commandees ?? 0} commandées</span>
          <Progress percent={Math.min(100, pctPaires ?? 0)} showInfo={pctPaires != null} format={() => `${pctPaires} %`} size="small" strokeColor="#0f7a70" />
        </div>
        <div className="da-tr__ratio">
          <span><b>{$(j.encaisse)}</b> encaissé / {$(j.montant_attendu)} attendus</span>
          <Progress percent={Math.min(100, pctMontant ?? 0)} showInfo={pctMontant != null} format={() => `${pctMontant} %`} size="small" strokeColor="#a86f14" />
        </div>
        <span className="da-tr__sub">Les ventes/encaissements du jour peuvent provenir de commandes ou créances antérieures.</span>
      </div>

      <div className="da-tr__card">
        <span className="da-tr__lbl">En livraison (prises en charge)</span>
        <span className="da-tr__big">{liv.commandes_confirmees ?? 0} <small>commande(s)</small></span>
        <span className="da-tr__sub">{liv.paires_confirmees ?? 0} paire(s) chez les livreurs</span>
        <span className="da-tr__sub">
          Affectées {liv.paires_affectees ?? 0} · reportées {liv.paires_reportees ?? 0} · à l’essai {liv.paires_essai ?? 0}
        </span>
      </div>

      <div className="da-tr__card da-tr__card--flu">
        <span className="da-tr__lbl">Fluidité des opérations</span>
        <div className="da-tr__chips">
          {fluChips.map((c) => (
            <span key={c.l} className={`da-tr__chip ${c.ok ? 'is-ok' : 'is-warn'}`}>
              {c.l} : <b>{c.v}</b>
            </span>
          ))}
        </div>
      </div>

      {pl.length > 0 && (
        <div className="da-tr__card da-tr__card--liv">
          <span className="da-tr__lbl">Par livreur — aujourd’hui</span>
          <div className="da-trliv-wrap">
            <table className="da-trliv-table">
              <thead>
                <tr>
                  <th>Livreur</th><th>Cmd en cours</th><th>Sorties auj.</th><th>Dehors</th>
                  <th>Vendues</th><th>CA réalisé</th><th>Encaissé</th>
                  <th>PEC (7 j)</th><th>Livr. (7 j)</th><th>Taux (7 j)</th>
                </tr>
              </thead>
              <tbody>
                {pl.map((l) => (
                  <tr key={l.id}>
                    {/* data-col : sous 900px la table devient une liste de cartes
                        (CSS) et les en-têtes de colonne disparaissent — chaque
                        chiffre porte alors son libellé, sinon « 3 · 7 · 0 » ne
                        veut plus rien dire. */}
                    <td className="da-trliv-nom">{l.nom}</td>
                    <td data-col="Cmd en cours">{l.commandes_en_cours}</td>
                    <td data-col="Sorties auj.">{l.paires_sorties_jour}</td>
                    <td data-col="Dehors">{l.paires_dehors}{l.paires_essai > 0 ? <small> (dont {l.paires_essai} à l’essai)</small> : null}</td>
                    <td data-col="Vendues">{l.paires_vendues}</td>
                    <td className="da-trliv-money" data-col="CA réalisé">{$(l.ca)}</td>
                    <td className="da-trliv-money" data-col="Encaissé">{$(l.encaisse)}</td>
                    <td data-col="PEC (7 j)">{l.flu_pec_h == null ? '—' : `${l.flu_pec_h} h`}</td>
                    <td data-col="Livr. (7 j)">{l.flu_livraison_h == null ? '—' : `${l.flu_livraison_h} h`}</td>
                    <td data-col="Taux (7 j)" className={l.flu_taux_pct != null && l.flu_taux_pct < 70 ? 'da-trliv-warn' : ''}>{l.flu_taux_pct == null ? '—' : `${l.flu_taux_pct} %`}</td>
                  </tr>
                ))}
              </tbody>
              {plTotal && pl.length > 1 && (
                <tfoot>
                  <tr>
                    <td className="da-trliv-nom">Total ({pl.length} livreurs)</td>
                    <td data-col="Cmd en cours">{plTotal.commandes_en_cours}</td>
                    <td data-col="Sorties auj.">{plTotal.paires_sorties_jour}</td>
                    <td data-col="Dehors">{plTotal.paires_dehors}{plTotal.paires_essai > 0 ? <small> (dont {plTotal.paires_essai} à l’essai)</small> : null}</td>
                    <td data-col="Vendues">{plTotal.paires_vendues}</td>
                    <td className="da-trliv-money" data-col="CA réalisé">{$(plTotal.ca)}</td>
                    <td className="da-trliv-money" data-col="Encaissé">{$(plTotal.encaisse)}</td>
                    {/* Les 3 colonnes de fluidité n'ont pas de total : une moyenne
                        de délais n'aurait pas de sens. En cartes, la cellule vide
                        disparaît d'elle-même (pas de data-col). */}
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <span className="da-tr__sub">Encaissé = paiements saisis par le livreur lui-même (un encaissement saisi au bureau n’y figure pas). Ventes directes des vendeurs exclues.</span>
        </div>
      )}
    </div>
  );
}

// ── Composant principal ─────────────────────────────────────────
// Recettes par cliente (demande proprio 15/07) : ventes CONFIRMÉES vs revenus
// ATTENDUS (commandes de la fenêtre, hors annulées) — mêmes conventions V4 que
// le reste du dashboard. `fenetre` : libellé affiché (30 j ici, période choisie
// côté cockpit).
export function RecettesClientes({ rc, fenetre = '30 derniers jours', drillStart, drillEnd }) {
  // Drill-down (demande proprio 20/07) : cliquer une cliente ouvre le détail de
  // ses achats (article × qté, prix, photo). Fenêtre = celle du bloc : passée
  // par l'appelant, sinon les 30 derniers jours (défaut du dashboard admin).
  const [drill, setDrill] = useState(null);   // { id_client, client }
  // Jour de KINSHASA (convention de toute l'app) — un téléphone réglé sur un
  // autre fuseau décalerait la fenêtre d'un jour.
  const jourKinshasa = (delta = 0) => {
    const p = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Africa/Kinshasa', year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date());
    if (!delta) return p;
    const d = new Date(`${p}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().slice(0, 10);
  };
  // Défaut = fenêtre EXACTE du bloc côté serveur (J-30 → aujourd'hui, 31 jours) :
  // à 30 jours, le plus vieux jour du bloc manquait dans le détail.
  const debut = drillStart || jourKinshasa(-30);
  const fin = drillEnd || jourKinshasa(0);

  if (!rc || !rc.items || !rc.items.length) {
    return <div className="da-recettes-vide">Aucune vente ni commande sur la période.</div>;
  }
  const $ = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-CD')} $`;
  return (
    <div className="da-recettes">
      <div className="da-recettes__meta">{rc.nb_clientes} cliente(s) · {fenetre} · cliquez une cliente pour le détail</div>
      <div className="da-recettes__wrap">
        <table>
          <thead>
            <tr><th>Cliente</th><th>Paires</th><th>Ventes confirmées</th><th>Attendu</th><th>Écart</th></tr>
          </thead>
          <tbody>
            {rc.items.map((c) => (
              <tr key={c.id_client} className={c.id_client ? "da-recettes__ligne" : undefined}
                onClick={() => c.id_client && setDrill({ id_client: c.id_client, client: c.client })}>
                <td className="da-recettes__nom">{c.client}</td>
                <td data-col="Paires">{c.paires}</td>
                <td className="da-recettes__ok" data-col="Ventes confirmées">{$(c.ventes)}</td>
                <td data-col="Attendu">{$(c.attendu)}</td>
                <td data-col="Écart" className={c.ecart > 0 ? 'da-recettes__reste' : ''}>{$(c.ecart)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="da-recettes__nom"><b>Total ({rc.nb_clientes})</b></td>
              <td data-col="Paires"><b>{rc.totaux.paires}</b></td>
              <td className="da-recettes__ok" data-col="Ventes confirmées"><b>{$(rc.totaux.ventes)}</b></td>
              <td data-col="Attendu"><b>{$(rc.totaux.attendu)}</b></td>
              <td data-col="Écart" className={rc.totaux.ecart > 0 ? 'da-recettes__reste' : ''}><b>{$(rc.totaux.ecart)}</b></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <VentesClienteModal
        open={!!drill}
        onClose={() => setDrill(null)}
        idClient={drill?.id_client}
        nom={drill?.client}
        start={debut}
        end={fin}
      />
    </div>
  );
}

// ── Pilotage ERP (choix proprio 15/07) ──────────────────────────────────────
const usd0 = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-CD')} $`;

// ② Objectif du mois : jauge + rythme actuel vs requis (façon Odoo).
export function ObjectifMois({ o }) {
  if (!o || !o.actif) return null;
  const atteint = o.pct >= 100;
  return (
    <div className="da-objectif">
      <div className="da-objectif__ligne">
        <b>{usd0(o.realise)}</b>
        <span>sur {usd0(o.objectif)} — {o.pct}%{atteint ? ' 🎉' : ''}</span>
      </div>
      <div className="da-objectif__jauge"><div style={{ width: `${Math.min(100, o.pct)}%` }} className={atteint ? 'ok' : ''} /></div>
      <div className="da-objectif__rythme">
        <span>Rythme actuel : <b>{usd0(o.rythme_actuel)}/j</b> ({o.jours_ecoules} j écoulés)</span>
        {!atteint && <span>Requis : <b>{usd0(o.requis_par_jour)}/j</b> ({o.jours_restants} j restants)</span>}
      </div>
    </div>
  );
}

// ① Balance âgée des créances : 3 tranches + clientes à relancer (façon SAP).
export function BalanceAgee({ b, onOuvrirCreances = null }) {
  if (!b) return null;
  const tons = ['ok', 'warn', 'crit'];
  return (
    <div className="da-agee">
      <div className="da-agee__tuiles">
        {b.tranches.map((t, i) => (
          <div key={t.cle} className={`da-agee__tuile is-${tons[i]}`}>
            <small>{t.label}</small>
            <b>{usd0(t.montant)}</b>
            <span>{t.n} créance(s)</span>
          </div>
        ))}
        <div className="da-agee__tuile is-total">
          <small>Total dehors</small>
          <b>{usd0(b.total.montant)}</b>
          <span>{b.total.n} créance(s)</span>
        </div>
      </div>
      {b.a_relancer.length > 0 && (
        <div className="da-agee__relances">
          <div className="da-agee__relances-titre">À relancer en premier</div>
          {b.a_relancer.map((c) => (
            <button key={c.id_client} type="button" onClick={onOuvrirCreances}>
              <span className="da-agee__cliente">{c.client}</span>
              <span className="da-agee__age">{c.age_max} j</span>
              <b>{usd0(c.solde)}</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Variante avec navigation vers le Centre des créances (Accueil admin).
function BalanceAgeeAvecNav({ b }) {
  const navigate = useNavigate();
  return <BalanceAgee b={b} onOuvrirCreances={() => navigate('/creances')} />;
}

// ③ Marge brute & panier moyen (couverture = part des paires au coût connu).
export function MargeBrute({ m, fenetre = '30 derniers jours' }) {
  if (!m) return null;
  return (
    <div className="da-marge">
      <div className="da-marge__cartes">
        <div><small>CA net · {fenetre}</small><b>{usd0(m.ca)}</b><span>{m.paires} paire(s) · {m.commandes} commande(s)</span></div>
        <div><small>Marge brute</small>
          <b>{m.marge === null ? '—' : usd0(m.marge)}</b>
          <span>{m.marge_pct === null ? 'aucun coût connu' : `${m.marge_pct}% du CA couvert`}</span>
        </div>
        <div><small>Panier moyen</small><b>{m.panier_moyen === null ? '—' : usd0(m.panier_moyen)}</b><span>{m.prix_moyen_paire === null ? '' : `${usd0(m.prix_moyen_paire)}/paire`}</span></div>
      </div>
      {m.couverture_pct < 100 && (
        <p className="da-marge__note">Coût d'achat connu sur {m.couverture_pct}% des paires vendues — complétez les coûts
          (Réceptions → Coûts d'achat) pour une marge complète.</p>
      )}
    </div>
  );
}

export default function DashboardAdmin({ data }) {
  if (!data) return null;
  const { hero, jour, temps_reel, kpis = [], chart_ca_30j = [], funnel, stock, health, top, alerts = [], recettes_clientes, balance_agee, objectif_mois, marge_30j } = data;

  return (
    <div className="da-root">
      <HeroBlock hero={hero} />

      <TempsReelBlock tr={temps_reel} jour={jour} />

      <div className="da-content">
        {kpis.length > 0 && (
          <Section title="Indicateurs clés">
            <div className="da-kpi-grid">
              {kpis.map(k => <KpiCard key={k.key} kpi={k} />)}
            </div>
          </Section>
        )}

        {objectif_mois?.actif && (
          <Section title="Objectif du mois">
            <ObjectifMois o={objectif_mois} />
          </Section>
        )}

        {chart_ca_30j.length > 0 && (
          <Section title="CA net — 30 derniers jours">
            <ChartCa data30j={chart_ca_30j} />
          </Section>
        )}

        {balance_agee && (
          <Section title="Balance âgée des créances">
            <BalanceAgeeAvecNav b={balance_agee} />
          </Section>
        )}

        {marge_30j && (
          <Section title="Marge brute & panier moyen — 30 jours">
            <MargeBrute m={marge_30j} />
          </Section>
        )}

        <Section title="Mix des ventes — 30 jours">
          <MixVentes />
        </Section>

        <div className="da-two-col">
          {funnel && (
            <Section title="Entonnoir opérationnel">
              <FunnelOps funnel={funnel} />
            </Section>
          )}
          {stock && (
            <Section title="Santé du stock">
              <StockHealth stock={stock} health={health} />
            </Section>
          )}
        </div>

        {top && (
          <Section title="Performances 30 jours">
            <TopRankings top={top} />
          </Section>
        )}

        {recettes_clientes && (
          <Section title="Recettes par cliente — 30 jours">
            <RecettesClientes rc={recettes_clientes} />
          </Section>
        )}

        {alerts !== undefined && (
          <Section title="Alertes actionnables">
            <AlertList alerts={alerts} />
          </Section>
        )}
      </div>
    </div>
  );
}
